import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { Home, CheckSquare, User, LogOut, Menu, X, Bell, ChevronRight, Zap } from 'lucide-react'

export type EmployeeSection = 'home' | 'tasks' | 'profile'

interface NavItem { id: EmployeeSection; label: string; icon: React.ElementType }

const NAV: NavItem[] = [
  { id: 'home',    label: 'Home',    icon: Home },
  { id: 'tasks',   label: 'My Tasks', icon: CheckSquare },
  { id: 'profile', label: 'Profile',  icon: User },
]

interface Props {
  active: EmployeeSection
  onNav: (s: EmployeeSection) => void
  children: React.ReactNode
}

export default function EmployeeLayout({ active, onNav, children }: Props) {
  const { currentUser, logout } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-5 border-b" style={{ borderColor: 'rgba(255,198,0,0.12)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#ffc600' }}>
          <Zap size={15} color="#000" fill="#000" />
        </div>
        <div>
          <p className="text-sm font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Clickbait</p>
          <p className="text-xs font-medium" style={{ color: '#ffc600', fontSize: '10px', letterSpacing: '0.08em' }}>WORKSPACE</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map(({ id, label, icon: Icon }) => {
          const isActive = active === id
          return (
            <button key={id} onClick={() => { onNav(id); setMobileOpen(false) }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-left transition-all"
              style={isActive ? {
                borderLeft: '2px solid #ffc600', color: '#ffc600',
                background: 'rgba(255,198,0,0.07)', borderRadius: '0 8px 8px 0',
              } : {
                borderLeft: '2px solid transparent', color: '#a0a0a0', borderRadius: '0 8px 8px 0',
              }}>
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {isActive && <ChevronRight size={13} className="flex-shrink-0" />}
            </button>
          )
        })}
      </nav>

      {/* User */}
      <div className="px-4 py-4 border-t" style={{ borderColor: 'rgba(255,198,0,0.12)' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{ background: '#ffc600', color: '#000' }}>
            {currentUser?.avatarInitials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{currentUser?.name}</p>
            <p className="text-xs truncate" style={{ color: '#555' }}>Team Member</p>
          </div>
        </div>
        <button onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all"
          style={{ color: '#cc3300', border: '1px solid rgba(204,51,0,0.2)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(204,51,0,0.08)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  )

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#000' }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-56 flex-shrink-0"
        style={{ background: '#0f0f0f', borderRight: '1px solid rgba(255,198,0,0.12)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-56 z-50"
            style={{ background: '#0f0f0f', borderRight: '1px solid rgba(255,198,0,0.12)' }}>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3"
          style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.12)' }}>
          <span className="font-bold text-white text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>
            {currentUser?.name}
          </span>
          <div className="flex items-center gap-3">
            <Bell size={18} style={{ color: '#a0a0a0' }} />
            <button onClick={() => setMobileOpen(v => !v)} style={{ color: '#fff' }}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  )
}
